<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useAuthStore } from '@/modules/auth/stores/auth.store'
import { useAppContextStore } from '@/stores/app-context.store'
import AppLayout from '@/layouts/AppLayout.vue'
import { useBrandingStore } from '@/stores/branding.store'

const authStore = useAuthStore()
const brandingStore = useBrandingStore()
const contextStore = useAppContextStore()
const route = useRoute()
const router = useRouter()

const isAuthenticated = computed(() => authStore.isAuthenticated)

// No mostrar AppLayout en rutas sin layout ni en rutas de guest (login, signup)
// Esto evita que el sidebar aparezca envolviendo el LoginView si el router
// queda atascado en /login con un token activo en el store.
const useLayout = computed(
  () => isAuthenticated.value && !route.meta.noLayout && !route.meta.guestOnly,
)

// Bloquear el render de cualquier vista hasta que el contexto esté resuelto.
// Esto evita el flash de la vista destino antes de la redirección a /onboarding.
// Para usuarios no autenticados (login, signup) siempre está listo.
const appReady = computed(
  () => !isAuthenticated.value || contextStore.loaded,
)

watch(
  isAuthenticated,
  async (authed) => {
    if (!authed) {
      contextStore.reset()
      return
    }

    // Super admin: no tiene studio propio, no necesita contexto de onboarding.
    // Ir directo al panel de administración.
    if (authStore.isSuperAdmin) {
      contextStore.markSuperAdmin()
      if (!route.path.startsWith('/admin')) {
        await router.push('/admin')
      }
      return
    }

    // Cargar branding y studios
    try {
      await brandingStore.loadStudios()
      await brandingStore.loadBranding()
    } catch {
      // ignorar errores de red en el arranque
    }

    // Cargar contexto de onboarding y trial.
    try {
      await contextStore.loadContext()
    } catch {
      // El store ya maneja el error internamente
    }

    // Si el onboarding no está completo, redirigir una sola vez al arrancar.
    if (
      !contextStore.loadError &&
      !contextStore.onboardingComplete &&
      route.path !== '/onboarding'
    ) {
      await router.push('/onboarding')
    }
  },
  { immediate: true },
)
</script>

<template>
  <div v-if="!appReady" class="app-loading">
    <div class="app-loading__spinner" />
  </div>
  <template v-else>
    <AppLayout v-if="useLayout">
      <RouterView />
    </AppLayout>
    <RouterView v-else />
  </template>
</template>

<style scoped>
.app-loading {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-background, #f9fafb);
  z-index: 9999;
}

.app-loading__spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: var(--color-primary, #6366f1);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
